import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Button } from "@mui/material";
import SearchableSelect from "../../../../components/SearchableSelect";
import { dedupeSortedStrings, normalizeString } from "./utils";

const tableStyles = {
  container: {
    overflowX: "auto",
    border: "1px solid #d8d8d8",
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 900,
  },
  headCell: {
    padding: "10px 12px",
    borderBottom: "1px solid #d8d8d8",
    textAlign: "left",
    fontSize: 13,
    fontWeight: 700,
    backgroundColor: "#f6f7f9",
  },
  cell: {
    padding: "10px 12px",
    borderBottom: "1px solid #efefef",
    verticalAlign: "top",
  },
  input: {
    width: "100%",
    minWidth: 220,
    height: 36,
    border: "1px solid #d0d0d0",
    borderRadius: 6,
    padding: "0 10px",
    fontSize: 14,
    boxSizing: "border-box",
  },
};

function TrainingMatrixTab({
  rows,
  setRows,
  quizzes,
  categories,
  jobClassifications,
  onSave,
  onExport,
  saving,
}) {
  const classificationOptions = useMemo(
    () => dedupeSortedStrings([...jobClassifications, ...rows.map((row) => row.jobClassification)]),
    [jobClassifications, rows]
  );

  const duplicateClassifications = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      const key = normalizeString(row.jobClassification);
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()]
      .filter(([, count]) => count > 1)
      .map(([key]) => key);
  }, [rows]);

  const updateRow = (index, patch) => {
    const next = [...rows];
    next[index] = { ...next[index], ...patch };
    setRows(next);
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        jobClassification: "",
        requiredQuizIds: [],
        requiredCategoryIds: [],
      },
    ]);
  };

  const removeRow = (index) => {
    setRows(rows.filter((_row, rowIndex) => rowIndex !== index));
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <Button variant="outlined" size="small" onClick={addRow}>
          Add Row
        </Button>
        <Button variant="contained" size="small" onClick={() => onSave(rows)} disabled={saving}>
          {saving ? "Saving..." : "Save Training Matrix"}
        </Button>
        <Button variant="outlined" size="small" onClick={onExport}>
          Export CSV
        </Button>
      </div>

      {duplicateClassifications.length > 0 && (
        <p style={{ margin: "0 0 10px", color: "#9b1c1c", fontSize: 13 }}>
          Duplicate job classifications found. Saving keeps the last row for each duplicate value.
        </p>
      )}

      <div style={tableStyles.container}>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              <th style={tableStyles.headCell}>Job Classification</th>
              <th style={tableStyles.headCell}>Required Training (Quizzes)</th>
              <th style={tableStyles.headCell}>Required Categories</th>
              <th style={tableStyles.headCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td style={tableStyles.cell} colSpan={4}>
                  No matrix rows yet. Add a row to start mapping requirements.
                </td>
              </tr>
            )}

            {rows.map((row, index) => (
              <tr key={`${row.jobClassification || "row"}-${index}`}>
                <td style={tableStyles.cell}>
                  <input
                    list="matrix-training-classifications"
                    style={tableStyles.input}
                    value={row.jobClassification || ""}
                    onChange={(event) =>
                      updateRow(index, { jobClassification: normalizeString(event.target.value) })
                    }
                  />
                </td>
                <td style={tableStyles.cell}>
                  <SearchableSelect
                    options={quizzes}
                    value={row.requiredQuizIds || []}
                    onChange={(values) =>
                      updateRow(index, { requiredQuizIds: dedupeSortedStrings(values) })
                    }
                    getOptionLabel={(option) => option.title || option.id || ""}
                    valueKey="id"
                    multiple
                    label="Required Quizzes"
                  />
                </td>
                <td style={tableStyles.cell}>
                  <SearchableSelect
                    options={categories}
                    value={row.requiredCategoryIds || []}
                    onChange={(values) =>
                      updateRow(index, { requiredCategoryIds: dedupeSortedStrings(values) })
                    }
                    getOptionLabel={(option) => option.title || option.id || ""}
                    valueKey="id"
                    multiple
                    label="Required Categories"
                  />
                </td>
                <td style={tableStyles.cell}>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => removeRow(index)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <datalist id="matrix-training-classifications">
        {classificationOptions.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>
    </div>
  );
}

TrainingMatrixTab.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      jobClassification: PropTypes.string,
      requiredQuizIds: PropTypes.arrayOf(PropTypes.string),
      requiredCategoryIds: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  setRows: PropTypes.func.isRequired,
  quizzes: PropTypes.arrayOf(PropTypes.object).isRequired,
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  jobClassifications: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSave: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

TrainingMatrixTab.defaultProps = {
  saving: false,
};

export default TrainingMatrixTab;
